import axios from "axios";

// ICAAL CRM API endpoint mapping based on apiKey
const API_ENDPOINTS = {
    DGN: "https://api.clients.icaal.co.uk/forms/form_pk_Z3HgH2BsHWeCLylzbwag6t4M",
    L24RZISO: "https://api.clients.icaal.co.uk/forms/form_pk_o0AAZga3zI8jvNUIBOhZNgXj",
    PBBCILG3: "https://api.clients.icaal.co.uk/forms/form_pk_kTCazSPjrTqi8WeOkvDKfQP9",
    FNVVMYYO: "https://api.clients.icaal.co.uk/forms/form_pk_PGXFlnWk0Qt2dPlzGYpQxZsA",
};

const landing_page_url = {
    DGN: "https://www.doubleglazingnetwork.com/",
    L24RZISO: "https://api.clients.icaal.co.uk/forms/form_pk_o0AAZga3zI8jvNUIBOhZNgXj",
    PBBCILG3: "https://api.clients.icaal.co.uk/forms/form_pk_kTCazSPjrTqi8WeOkvDKfQP9",
    FNVVMYYO: "https://www.progresswindows.com/",
};

const TRUFRAME_API_BASE = "https://api.truframe.co.uk:1985";

/**
 * Fetch the quote PDF for a given installer guid and enquiry uid from the
 * TruFrame GetReport endpoint.
 * Returns a File object (PDF blob) or null if the fetch fails.
 */
const fetchQuotePdf = async (tradeCustomerGuid, enquiryUid) => {
    if (!tradeCustomerGuid || !enquiryUid) return null;
    const url = `${TRUFRAME_API_BASE}/pricingapi/enquiry/${tradeCustomerGuid}/GetReport?enquiryUid=${enquiryUid}`;
    try {
        const response = await axios.get(url, { responseType: "blob" });
        return new File([response.data], `quote-${tradeCustomerGuid}-${enquiryUid}.pdf`, {
            type: "application/pdf",
        });
    } catch (err) {
        console.error(`fetchQuotePdf: failed for installer ${tradeCustomerGuid}`, err);
        return null;
    }
};

const buildPayload = (formValues, additionalItem, params, apiKey) => {
    console.log("Form values:", formValues);
    console.log("Form additional", additionalItem);
    let utmSource = "";
    const searchParams = params && new URLSearchParams(params);
    if (params) {
        utmSource = searchParams.get("utm_source");
        console.log(utmSource);
        console.log(searchParams);
    }

    // Extract form values
    const {
        name = "",
        email = "",
        telephone = "",
        postcode = "",
        TandC = false,
        consentCommunication = false,
    } = formValues;

    // Split name into first and last name
    const fullName = (name || "").trim();
    const nameParts = fullName.split(/\s+/);
    const firstName = nameParts[0] || "";
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "NA";

    // Build payload with required fields
    const payload = {
        first_name: firstName,
        last_name: lastName,
        postcode: postcode || sessionStorage.getItem("customerPostcode") || "LE13 1BS",
        email: email,
        phone: telephone,
        consent_terms: TandC,
        consent_communication: consentCommunication,
        icaal_tracking: params ? JSON.stringify(Object.fromEntries(searchParams.entries())) : null,
        icaal_landing_url: landing_page_url[apiKey],
        icaal_referrer_url: utmSource && utmSource,
    };

    // Add additional item data
    if (additionalItem) {
        payload.item_id = additionalItem.id;
        payload.item_name = additionalItem.name;
    }

    return payload;
};

/**
 * Send the enquiry form to the ICAAL CRM API.
 *
 * @param {object} formValues         - Contact form values
 * @param {object} additionalItem     - Optional additional item data (from this.props)
 * @param {string} apiKey             - Customer API key (e.g. "DGN")
 * @param {string} params             - Raw URL search params string
 * @param {Array}  pricedEnquiries    - Array of { tradeCustomerGuid, enquiryUid } from BatchPriceEnquiry
 *
 * Up to 3 PDFs are fetched in parallel (one per installer) and attached as
 * quote_summary, quote_summary_2, quote_summary_3.
 */
export const sendEnquiryToApi = async (formValues, additionalItem, apiKey, params, pricedEnquiries = []) => {
    console.log("Testing API:", apiKey);
    // Check if this apiKey should use the external API
    const endpoint = API_ENDPOINTS[apiKey];

    if (!endpoint) {
        // API not used for this apiKey
        return { success: true, skipped: true, message: "No API configured for this apiKey" };
    }

    try {
        // Build the payload
        const payload = buildPayload(formValues, additionalItem, params, apiKey);

        // Convert payload to FormData
        const formData = new FormData();

        Object.keys(payload).forEach((key) => {
            if (payload[key] !== undefined && payload[key] !== null) {
                formData.append(key, payload[key]);
            }
        });

        console.log("apiService: pricedEnquiries received:", pricedEnquiries);

        // Fetch up to 3 PDFs in parallel — one per installer
        // Each entry is { tradeCustomerGuid, enquiryUid } from BatchPriceEnquiry
        if (Array.isArray(pricedEnquiries) && pricedEnquiries.length > 0) {
            const toFetch = pricedEnquiries.slice(0, 3);

            const pdfFiles = await Promise.all(
                toFetch.map(({ tradeCustomerGuid, enquiryUid }) => {
                    if (!tradeCustomerGuid || !enquiryUid) {
                        console.warn(
                            "apiService: skipping entry with missing tradeCustomerGuid or enquiryUid",
                            { tradeCustomerGuid, enquiryUid }
                        );
                        return Promise.resolve(null);
                    }
                    console.log(
                        `apiService: fetching PDF for installer ${tradeCustomerGuid} with enquiryUid ${enquiryUid}`
                    );
                    return fetchQuotePdf(tradeCustomerGuid, enquiryUid);
                })
            );

            // Attach each successfully fetched PDF using CRM-expected field names
            pdfFiles.forEach((pdfFile, index) => {
                if (pdfFile) {
                    const fieldName =
                        index === 0 ? "quote_summary" : `quote_summary_${index + 1}`;
                    formData.append(fieldName, pdfFile, pdfFile.name);
                }
            });
        } else {
            console.warn("apiService: no pricedEnquiries provided — no PDFs will be attached");
        }

        // Debug: log what's actually in the FormData before sending
        console.log("Sending FormData to:", endpoint);
        for (let [key, value] of formData.entries()) {
            if (value instanceof File) {
                console.log(`  ${key}: [File] ${value.name} (${value.size} bytes, ${value.type})`);
            } else {
                console.log(`  ${key}:`, value);
            }
        }

        // Send request as FormData
        const response = await axios.post(endpoint, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        console.log("SUCCESS", response.data);
        return {
            success: true,
            data: response.data,
            message: "Form submitted successfully",
        };
    } catch (error) {
        console.error("Error sending enquiry to API:", error);

        // Safe fallback without optional chaining
        const apiError =
            error.response && error.response.data ? error.response.data : error.message;

        return {
            success: false,
            error: apiError,
            message: "Failed to submit form",
        };
    }
};



// WEBPACK FOOTER //
// ./src/containers/QuoteForm/apiService.js